import { getCessionsSummary } from 'lib/api/business';
import { CessionData } from 'lib/api/business/types';
import { useState } from 'react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

type ProcessedData = {
  id: string;
  name: string;
  identifier: string;
  amount: number;
  cancelAmount: number;
  creditNoteAmount: number;
  cancelPercentage: number;
  creditNotePercentage: number;
};

type MonthSumByIdentifier = Record<string, ProcessedData>;

type Metrics = {
  average: number;
  totalAmount: number;
  totalCancelAmount: number;
  cancelPercentage: number;
  totalCreditNoteAmount: number;
  creditNotePercentage: number;
};

const useData = (months: string[], selected: string[]) => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { data: cessionsData } = getCessionsSummary(dispatch, id);
  const [factoringData, setFactoringData] = useState<ProcessedData[]>();
  const [payersData, setPayersData] = useState<ProcessedData[]>();
  const [metrics, setMetrics] = useState<Metrics>();

  const processFactoringData = () => {
    const data = months.reduce((acc: CessionData[], month: string) => {
      const monthData = cessionsData?.dataFactorings?.[month];
      if (monthData) return acc.concat(monthData);
      return acc;
    }, []);

    if (data.length === 0) return;

    const monthsSumByIdentifier: MonthSumByIdentifier = data.reduce(
      (acc: MonthSumByIdentifier, monthData: CessionData) => {
        if (!acc[monthData.identifier]) {
          acc[monthData.identifier] = {
            id: monthData.identifier,
            name: monthData.name,
            identifier: monthData.identifier,
            amount: 0,
            cancelAmount: 0,
            creditNoteAmount: 0,
            cancelPercentage: 0,
            creditNotePercentage: 0,
          };
        }
        for (const payer of monthData?.payersData || []) {
          if (selected.includes(payer.identifier) || selected.length === 0) {
            acc[monthData.identifier].amount += payer.amount;
            acc[monthData.identifier].cancelAmount += payer.cancelAmount;
            acc[monthData.identifier].creditNoteAmount +=
              payer.creditNoteAmount;
          }
        }
        return acc;
      },
      {},
    );

    Object.keys(monthsSumByIdentifier).forEach((key) => {
      const payer = monthsSumByIdentifier[key];
      payer.cancelPercentage =
        payer.amount > 0 ? payer.cancelAmount / payer.amount : 0;
      payer.creditNotePercentage =
        payer.amount > 0 ? payer.creditNoteAmount / payer.amount : 0;
    });

    const processedData = Object.values(monthsSumByIdentifier);
    setFactoringData(processedData);
    return processedData;
  };

  const processPayersData = () => {
    const data = months.reduce((acc: CessionData[], month: string) => {
      const monthData = cessionsData?.dataPayers?.[month];
      if (monthData) return acc.concat(monthData);
      return acc;
    }, []);

    if (data.length === 0) return;

    const monthsSumByIdentifier: MonthSumByIdentifier = data.reduce(
      (acc: MonthSumByIdentifier, monthData: CessionData) => {
        if (!acc[monthData.identifier]) {
          acc[monthData.identifier] = {
            id: monthData.identifier,
            name: monthData.name,
            identifier: monthData.identifier,
            amount: 0,
            cancelAmount: 0,
            creditNoteAmount: 0,
            cancelPercentage: 0,
            creditNotePercentage: 0,
          };
        }
        acc[monthData.identifier].amount += monthData.amount;
        acc[monthData.identifier].cancelAmount += monthData.cancelAmount;
        acc[monthData.identifier].creditNoteAmount +=
          monthData.creditNoteAmount;
        return acc;
      },
      {},
    );

    Object.keys(monthsSumByIdentifier).forEach((key) => {
      const payer = monthsSumByIdentifier[key];
      payer.cancelPercentage =
        payer.amount > 0 ? payer.cancelAmount / payer.amount : 0;
      payer.creditNotePercentage =
        payer.amount > 0 ? payer.creditNoteAmount / payer.amount : 0;
    });

    const processedData = Object.values(monthsSumByIdentifier);
    setPayersData(processedData);
    return processedData;
  };

  const processMetrics = (processedPayersData: ProcessedData[]) => {
    let totalAmount = 0;
    let totalCancelAmount = 0;
    let totalCreditNoteAmount = 0;
    processedPayersData.forEach((payer) => {
      if (selected.includes(payer.identifier) || selected.length === 0) {
        totalAmount += payer.amount;
        totalCancelAmount += payer.cancelAmount;
        totalCreditNoteAmount += payer.creditNoteAmount;
      }
    });
    let monthsToDivide = months.length;
    if (months[0] === 'Últimos 24M') monthsToDivide = 24;
    if (months[0] === 'Últimos 12M') monthsToDivide = 12;
    setMetrics({
      average: totalAmount / monthsToDivide,
      totalAmount: totalAmount,
      totalCancelAmount: totalCancelAmount,
      cancelPercentage: parseFloat(
        ((totalCancelAmount / totalAmount) * 100).toFixed(2),
      ),
      totalCreditNoteAmount: totalCreditNoteAmount,
      creditNotePercentage: parseFloat(
        ((totalCreditNoteAmount / totalAmount) * 100).toFixed(2),
      ),
    });
  };

  useEffect(() => {
    processFactoringData() || [];
    const processedPayersData = processPayersData() || [];
    processMetrics(processedPayersData);
  }, [months, selected, cessionsData]);

  return { factoringData, payersData, metrics };
};

export default useData;
